import cx from 'classnames';
import React, { useRef, useEffect } from 'react';
import { Dashboard, Summary } from '|/app/services';
import ExitWarningModal from '|/components/verifx/components/ExitWarningModal';
import styles from './navigation-rail.module.css';
import useNavRailScroll from '../../hooks/use-nav-rail-scroll';
import { useNavigationRailVm } from '../../hooks/use-navigation-rail-vm';
import { CancelOrderModal, EditOrderModal as EditOrderModalDS } from '../modals';
import CancelOrderSection from '../sections/cancel-order-section';
import NavigationSection from '../sections/navigation-section';
import VehicleSection from '../sections/vehicle-section';

interface NavigationRailProps {
  dashboard: Dashboard | null;
  summary: Summary | null;
  onNavigate?: () => void;
}

let lastStepName = '';

const NavigationRail: React.FC<NavigationRailProps> = ({ dashboard, summary, onNavigate }) => {
  const navRailRef = useRef<HTMLElement>(null);

  const {
    vehicle,
    deliveryType,
    orderedSteps,
    latestStep,
    sectionsArray,
    route,
    pathname,
    stepUrlMap,
    isEditOrderModalDSShown,
    isCancelOrderModalDSShown,
    isKiqExitWarningModalNavRailShown,
    editingSectionName,
    targetStep,
    getSectionStatusMappings,
    handleEditClick,
    handleCancelClick,
    handleStepClick,
    handleModalClose,
    handleKiqLeave,
    routeNameComponentLookup,
    currentSection,
    viewedSection
  } = useNavigationRailVm({ dashboard, summary, onNavigate });

  useEffect(() => {
    if (pathname !== '/next-step') {
      lastStepName = pathname;
    }
  }, [pathname]);

  const isNextStepRoute = pathname === '/next-step';
  const effectivePathname = isNextStepRoute ? lastStepName || pathname : pathname;

  useNavRailScroll({ latestStep, navRailRef });

  if (dashboard === null || summary === null) {
    return null;
  }

  if (latestStep && latestStep.sectionName === 'Dashboard') {
    return null;
  }

  // Treatment users in section 2 live on /checkout and /steps/insurance-options,
  // neither of which renders the rail. Hide it on transit routes (/next-step) too
  // so the rail doesn't flash with a state the user otherwise never sees.
  // Driver returns sectionName "Checkout" for section 2 in treatment.
  const isInCheckItOutTreatment =
    summary.experiments?.some(e => e.key === 'pp-check-it-out' && e.bucket === 'treatment') ??
    false;
  const isViewingStep = pathname.startsWith('/steps/');
  if (isInCheckItOutTreatment && latestStep?.sectionName === 'Checkout' && !isViewingStep) {
    return null;
  }

  return (
    <aside
      ref={navRailRef}
      className={cx(styles.navigationRail, isNextStepRoute && styles.noScroll)}
    >
      <VehicleSection vehicle={vehicle} />
      {sectionsArray.map(section => (
        <NavigationSection
          key={section.sectionNumber}
          sectionName={section.sectionName}
          sectionNumber={section.sectionNumber}
          steps={section.steps}
          latestStep={latestStep}
          pathname={effectivePathname}
          stepUrlMap={stepUrlMap}
          route={route}
          statusMappings={getSectionStatusMappings(section.sectionName)}
          routeNameComponentLookup={routeNameComponentLookup}
          currentSection={currentSection}
          handleEditClick={handleEditClick}
          handleStepClick={handleStepClick}
          allSteps={orderedSteps}
          allSections={sectionsArray}
          viewedSection={viewedSection}
        />
      ))}
      <CancelOrderSection handleCancelClick={handleCancelClick} />

      <CancelOrderModal
        isOpen={isCancelOrderModalDSShown}
        onClose={() => {
          handleModalClose('cancel');
        }}
      />

      <EditOrderModalDS
        isOpen={isEditOrderModalDSShown}
        onClose={() => {
          handleModalClose('edit');
        }}
        deliveryPreference={deliveryType}
        editingSectionName={editingSectionName}
        targetStep={targetStep}
        currentSectionName={latestStep?.sectionName}
      />

      <ExitWarningModal
        isOpen={isKiqExitWarningModalNavRailShown}
        fromNavRail={true}
        outOfAttempts={false}
        onLeave={handleKiqLeave}
        onClose={() => {
          handleModalClose('kiq');
        }}
      />
    </aside>
  );
};

export default NavigationRail;
